import storageSession from 'redux-persist/lib/storage/session';
import storage from 'redux-persist/lib/storage';
import { PersistedState } from 'redux-persist';

/**
 * Creates a migration function that resets the slice to initial state
 * if the stored version doesn't match the current version.
 *
 * HOW TO USE:
 * 1. When you change the shape of a reducer's state, bump its version constant
 * 2. The old persisted state will be automatically reset to initial state
 * 3. Other slices remain unaffected
 *
 * Example:
 * ```
 * const MY_REDUCER_VERSION = 2; // Bump this when state shape changes
 *
 * export const myReducerConfig = {
 *   key: 'myReducer',
 *   version: MY_REDUCER_VERSION,
 *   storage,
 *   migrate: createVersionedMigration(MY_REDUCER_VERSION),
 * };
 * ```
 */
export function createVersionedMigration<T>(currentVersion: number) {
  return (state: PersistedState, version?: number): Promise<PersistedState> => {
    // If versions don't match, return undefined to reset to initial state
    if (version !== currentVersion) {
      console.log(
        `Version mismatch detected. Stored: ${version}, Current: ${currentVersion}. Resetting slice to initial state.`,
      );
      // Return a resolved promise with undefined to trigger initial state
      return Promise.resolve(undefined as any);
    }
    // If versions match, return the state as-is
    return Promise.resolve(state);
  };
}

// Version constants - bump these when changing state shape
const REGISTRATION_VERSION = 1;
const ADD_MACHINE_VERSION = 1;
const SELECTED_ACCOUNT_VERSION = 1;
const VERIFY_MACHINE_VERSION = 1;
const MACHINE_DETAILS_VERSION = 1;
const MISC_VERSION = 1;

export const registrationPersistConfig = {
  key: 'registration',
  version: REGISTRATION_VERSION,
  storage: storageSession,
  migrate: createVersionedMigration(REGISTRATION_VERSION),
};

export const addMachinePersistConfig = {
  key: 'addMachine',
  version: ADD_MACHINE_VERSION,
  storage: storageSession,
  migrate: createVersionedMigration(ADD_MACHINE_VERSION),
};

export const selectedAccountConfig = {
  key: 'selectedAccount',
  version: SELECTED_ACCOUNT_VERSION,
  storage,
  migrate: createVersionedMigration(SELECTED_ACCOUNT_VERSION),
};

export const verifyMachinePersistConfig = {
  key: 'verifyMachine',
  version: VERIFY_MACHINE_VERSION,
  storage,
  migrate: createVersionedMigration(VERIFY_MACHINE_VERSION),
};

export const machineDetailsPersistConfig = {
  key: 'machineDetails',
  version: MACHINE_DETAILS_VERSION,
  storage,
  migrate: createVersionedMigration(MACHINE_DETAILS_VERSION),
};

export const miscPersistConfig = {
  key: 'misc',
  version: MISC_VERSION,
  storage,
  migrate: createVersionedMigration(MISC_VERSION),
};
